import { EmailPlansManagerService } from './services/EmailPlansManagerService';
import { TranslationsService } from './services/TranslationsService';
import type { EmailPlansUrlAdapter, Init } from './types';
import { managerHelpers } from '../../helpers';
import type { MergedDataReturnType, MergedHandlersReturnType } from '../../types';
import type { WidgetLifecycle } from '../WidgetLifecycle';
import { WithApi } from '../WithApi';

export type EmailPlansData = MergedDataReturnType<[EmailPlansManagerService, TranslationsService]>;

export type EmailPlansHandlers = MergedHandlersReturnType<[EmailPlansManagerService, TranslationsService]>;

type TApi = ReturnType<EmailPlansManagerService['api']> & ReturnType<TranslationsService['api']>;

export class EmailPlansManager extends WithApi<TApi> {
  constructor(private urlAdapter: EmailPlansUrlAdapter) {
    super();
  }

  public emailPlansInit = ({ pageStore, queries, t, initialPlanType, flags }: Init) => {
    const [
      planGroupQuery,
      planDetailQuery,
      paymentPlanQuery,
      extendedBillingAccountQuery,
      userRoleQuery,
      hasScheduledDowngradeQuery,
      suspensionInfoQuery,
      addOnsQuery,
    ] = queries;

    const addOnsBundlePending = flags.isSmsBundle ? addOnsQuery.isLoading : false;
    const pending =
      planGroupQuery.isPending ||
      planDetailQuery.isPending ||
      paymentPlanQuery.isPending ||
      extendedBillingAccountQuery.isPending ||
      userRoleQuery.isPending ||
      hasScheduledDowngradeQuery.isLoading ||
      suspensionInfoQuery?.isLoading ||
      addOnsBundlePending ||
      // Hold 'pending' while the upgrade-plan-promotion query is in-flight for a paid Micro Email
      // user. This ensures onMount (and the initial rating call) only fires after microEmail3PlGroupId
      // has resolved to 109, preventing a planGroupId: 379 rating that causes a backend 404.
      Boolean(flags.isPromotionPending);
    // isCoreMutationLockPending is intentionally excluded from `pending`.
    // The Stripe customer metadata fetch should not block the plan cards from
    // rendering. While the lock is resolving, isSingleBillChildAccount is false
    // (via isConfirmedCoreMutationLockedAccount) so selection is already disabled
    // via getIsDisabled() and onSelectPlanClick until the query settles.
    const error =
      planGroupQuery.error ||
      planDetailQuery.error ||
      paymentPlanQuery.error ||
      extendedBillingAccountQuery.error ||
      userRoleQuery.error ||
      hasScheduledDowngradeQuery.error ||
      suspensionInfoQuery?.error ||
      (flags.isSmsBundle ? addOnsQuery.error : null);

    if (pending) {
      return {
        status: 'pending',
        data: null,
        handlers: null,
        lifecycle: {} as WidgetLifecycle,
      } as const;
    }

    if (error) {
      return {
        status: 'error',
        data: error,
        handlers: {
          retry: () => {
            managerHelpers.retryQueries(queries);
          },
        },
        lifecycle: {} as WidgetLifecycle,
      } as const;
    }

    const translations = new TranslationsService();
    const emailPlansManagerService = new EmailPlansManagerService(
      pageStore,
      planGroupQuery.data,
      planDetailQuery.data,
      t,
      this.urlAdapter,
      paymentPlanQuery.data,
      extendedBillingAccountQuery.data,
      userRoleQuery.data,
      flags.isCanceled,
      initialPlanType,
      !!hasScheduledDowngradeQuery.data,
      flags.isMWebVariantEnabled,
      suspensionInfoQuery?.data,
      flags.hasStripeOverdue,
      flags.hasCharmPricing,
      flags.isValueFocusedMWebPlanSelectionEnabled,
      addOnsQuery.data,
      flags.isSmsBundle ?? false,
      flags.isSingleBillChildAccount,
      flags.isMicroEmail ?? false,
      flags.microEmail3PlGroupId,
      flags.isPlanRestructureExperiment ?? false,
      flags.shouldIncludeNon3plPlans ?? flags.shouldUseModernPayflowForLegacyPlans ?? false
    );

    this.initApi({
      ...emailPlansManagerService.api(),
      ...translations.api(),
    });

    return {
      status: 'loaded',
      data: {
        ...emailPlansManagerService.data(),
        ...translations.data(),
      },
      handlers: {
        ...emailPlansManagerService.handlers(),
        ...translations.handlers(),
      },
      lifecycle: {
        onMount() {
          emailPlansManagerService.onMount();
        },
      } as WidgetLifecycle,
    } as const;
  };
}
